import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

import Button from './Button';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigationItems = [
    { name: 'Home', path: '/homepage', icon: 'Home' },
    { name: 'Contact', path: '/contact', icon: 'MessageCircle' },
    { name: 'Privacy', path: '/privacy-policy', icon: 'Shield' },
    { name: 'Terms', path: '/terms-of-service', icon: 'FileText' },
  ];

  const moreItems = [
    { name: 'Email Template', path: '/coming-soon-email-template', icon: 'Mail' },
  ];

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const handleWaitlistClick = () => {
    closeMenu(); // Close mobile menu if open
    
    // If we're on homepage, scroll to waitlist section
    if (location?.pathname === '/homepage' || location?.pathname === '/') {
      const waitlistElement = document.querySelector('[data-waitlist-section]');
      if (waitlistElement) {
        waitlistElement?.scrollIntoView({ 
          behavior: 'smooth',
          block: 'center'
        });
      }
    } else {
      // Navigate to homepage and scroll to waitlist
      window.location.href = '/homepage#waitlist';
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-background/95 backdrop-blur-md shadow-soft border-b border-border' 
          : 'bg-black/30 backdrop-blur-sm'
      }`}
    >
      <div className="w-full">
        <div className="flex items-center justify-between h-20 px-6 lg:px-8">
          {/* Navigation - Left Side */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems?.slice(0, 2)?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-bold transition-all duration-300 smooth-transition ${
                  isActivePath(item?.path)
                    ? 'bg-primary text-primary-foreground shadow-soft'
                    : isScrolled
                      ? 'text-foreground hover:bg-muted hover:text-primary'
                      : 'hover:bg-white/20'
                }`}
                style={!isScrolled && !isActivePath(item?.path) ? {color: '#F97316', textShadow: '0 1px 4px rgba(0,0,0,0.8)'} : {}}
              >
                <Icon name={item?.icon} size={16} />
                <span>{item?.name}</span>
              </Link>
            ))}
          </nav>

          {/* Centered Logo */}
          <Link 
            to="/homepage" 
            className="flex items-center justify-center group absolute left-1/2 transform -translate-x-1/2"
            onClick={closeMenu}
          >
            <div className="relative flex flex-col items-center px-4 py-1.5 transition-all duration-300">
              <span className="text-lg font-black tracking-widest uppercase group-hover:opacity-90 transition-opacity duration-300" style={{color: '#F97316', textShadow: '0 1px 6px rgba(0,0,0,0.6)', letterSpacing: '0.18em', fontFamily: 'inherit'}}>CYNIZÉE</span>
              <span className="text-xs font-bold tracking-widest uppercase mt-0.5 group-hover:opacity-90 transition-opacity duration-300" style={{color: '#F97316', textShadow: '0 1px 4px rgba(0,0,0,0.5)', letterSpacing: '0.15em'}}>Organizing Chaos</span>
            </div>
          </Link>

          {/* Navigation & CTA - Right Side */}
          <div className="hidden lg:flex items-center space-x-1">
            {navigationItems?.slice(2)?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-bold transition-all duration-300 smooth-transition ${
                  isActivePath(item?.path)
                    ? 'bg-primary text-primary-foreground shadow-soft'
                    : isScrolled
                      ? 'text-foreground hover:bg-muted hover:text-primary'
                      : 'hover:bg-white/20'
                }`}
                style={!isScrolled && !isActivePath(item?.path) ? {color: '#F97316', textShadow: '0 1px 4px rgba(0,0,0,0.8)'} : {}}
              >
                <Icon name={item?.icon} size={16} />
                <span>{item?.name}</span>
              </Link>
            ))}
            
            {/* More Dropdown */}
            <div className="relative group">
              <button className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-bold transition-all duration-300 ${
                isScrolled
                  ? 'text-foreground hover:bg-muted hover:text-primary'
                  : 'hover:bg-white/20'
              }`}
              style={!isScrolled ? {color: '#F97316', textShadow: '0 1px 4px rgba(0,0,0,0.8)'} : {}}>
                <Icon name="MoreHorizontal" size={16} />
                <span>More</span>
                <Icon name="ChevronDown" size={14} className="group-hover:rotate-180 transition-transform duration-300" />
              </button>
              
              <div className="absolute right-0 top-full mt-2 w-48 bg-popover border border-border rounded-lg shadow-elevation opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                <div className="py-2">
                  {moreItems?.map((item) => (
                    <Link
                      key={item?.path}
                      to={item?.path}
                      className={`flex items-center space-x-3 px-4 py-2 text-sm hover:bg-muted transition-colors duration-200 ${
                        isActivePath(item?.path)
                          ? 'text-primary font-medium' :'text-popover-foreground'
                      }`}
                    >
                      <Icon name={item?.icon} size={16} />
                      <span>{item?.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* CTA Button - Desktop */}
            <Button 
              variant="default" 
              className="bg-accent hover:bg-accent/90 text-accent-foreground font-heading font-semibold ml-4"
              iconName="Bell"
              iconPosition="right"
              iconSize={16}
              onClick={handleWaitlistClick}
            >
              Join Waitlist
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors duration-200"
            aria-label="Toggle menu"
          >
            <Icon 
              name={isMenuOpen ? "X" : "Menu"} 
              size={24} 
              className="text-foreground transition-transform duration-300"
            />
          </button>
        </div>

        {/* Mobile Navigation */}
        <div 
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-spring ${
            isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <nav className="px-6 py-4 bg-card border-t border-border">
            <div className="space-y-2">
              {[...navigationItems, ...moreItems]?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={closeMenu}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActivePath(item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-card-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={18} />
                  <span>{item?.name}</span>
                </Link>
              ))}
            </div>
            
            <div className="mt-4 pt-4 border-t border-border">
              <Button 
                variant="default" 
                fullWidth
                className="bg-accent hover:bg-accent/90 text-accent-foreground font-heading font-semibold"
                iconName="Bell"
                iconPosition="right"
                iconSize={16}
                onClick={handleWaitlistClick}
              >
                Join Waitlist
              </Button>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;